import React from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const fmt = (val) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2 })?.format(val);

const SCENARIOS = [
  { key: "base", label: "Escenario Base", change: 0, icon: "Minus", colorClass: "text-slate-600", bgClass: "bg-slate-50", borderClass: "border-slate-200" },
  { key: "down5", label: "Caída -5%", change: -5, icon: "TrendingDown", colorClass: "text-amber-600", bgClass: "bg-amber-50", borderClass: "border-amber-200" },
  { key: "down10", label: "Caída -10%", change: -10, icon: "TrendingDown", colorClass: "text-red-600", bgClass: "bg-red-50", borderClass: "border-red-200" },
  { key: "down15", label: "Caída -15%", change: -15, icon: "AlertCircle", colorClass: "text-red-700", bgClass: "bg-red-100", borderClass: "border-red-300" },
  { key: "up5", label: "Subida +5%", change: 5, icon: "TrendingUp", colorClass: "text-emerald-600", bgClass: "bg-emerald-50", borderClass: "border-emerald-200" },
  { key: "up10", label: "Subida +10%", change: 10, icon: "TrendingUp", colorClass: "text-emerald-700", bgClass: "bg-emerald-100", borderClass: "border-emerald-300" },
];

function getStressInterpretation(stressResults, baseResult) {
  if (!stressResults || !baseResult) return null;
  const base = stressResults?.base;
  const down15 = stressResults?.down15;
  const up10 = stressResults?.up10;
  const drop = base - down15;
  const dropPct = base > 0 ? ((drop / base) * 100)?.toFixed(1) : 0;
  const gain = up10 - base;

  let riskMsg = "";
  if (drop > base * 0.12) {
    riskMsg = "⚠️ Alta sensibilidad al precio: una caída del 15% reduciría el precio máximo de compra en más del 12%. Considera reducir el margen objetivo o negociar condiciones de compra más conservadoras.";
  } else if (drop > base * 0.08) {
    riskMsg = "Sensibilidad moderada al precio. Una caída del 15% impacta entre el 8–12% del precio de compra. Mantén un buffer de caja suficiente para absorber volatilidad.";
  } else {
    riskMsg = "✅ Baja sensibilidad al precio. El lote es relativamente resistente a caídas del benchmark. Los costes fijos (logística, procesamiento) amortiguan bien el impacto.";
  }

  return { drop: fmt(drop), dropPct, gain: fmt(gain), riskMsg };
}

export default function StressTestPanel({ result, onRunStressTest, isRunning, stressResults }) {
  if (!result) {
    return (
      <div className="card p-4 md:p-6 flex flex-col items-center justify-center min-h-[180px] gap-3">
        <Icon name="FlaskConical" size={28} color="var(--color-muted-foreground)" strokeWidth={1.5} />
        <p className="text-sm text-center" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
          Calcule un precio primero para ejecutar el stress test
        </p>
      </div>
    );
  }

  const interpretation = getStressInterpretation(stressResults, result);

  return (
    <div className="card p-4 md:p-6 flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Icon name="FlaskConical" size={18} color="var(--color-accent)" strokeWidth={2} />
          <h3 className="font-heading font-semibold text-base" style={{ color: "var(--color-text-primary)" }}>
            Stress Test de Mercado
          </h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Play"
          iconPosition="left"
          loading={isRunning}
          onClick={onRunStressTest}
        >
          Ejecutar Stress Test
        </Button>
      </div>
      {stressResults ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {SCENARIOS?.map((sc) => {
              const price = stressResults?.[sc?.key];
              const diff = price - result?.maxPurchasePrice;
              return (
                <div
                  key={sc?.key}
                  className={`rounded-lg border p-3 ${sc?.bgClass} ${sc?.borderClass}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name={sc?.icon} size={14} color="currentColor" className={sc?.colorClass} strokeWidth={2} />
                    <span className={`text-xs font-semibold ${sc?.colorClass}`} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                      {sc?.label}
                    </span>
                  </div>
                  <p className={`font-data font-bold text-lg ${sc?.colorClass}`}>{fmt(price)}</p>
                  {sc?.change !== 0 && (
                    <p className="text-xs mt-0.5 font-data" style={{ color: diff < 0 ? "var(--color-error)" : "var(--color-success)" }}>
                      {diff > 0 ? "+" : ""}{fmt(diff)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Interpretation block */}
          <div className="rounded-lg border p-4 flex flex-col gap-3" style={{ borderColor: "var(--color-border)", background: "var(--color-muted)" }}>
            <div className="flex items-center gap-2">
              <Icon name="BookOpen" size={15} color="var(--color-accent)" strokeWidth={2} />
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>Cómo interpretar estos resultados</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              <div className="flex flex-col gap-1">
                <span className="font-semibold" style={{ color: "var(--color-text-primary)" }}>Precio por escenario</span>
                <span style={{ color: "var(--color-text-secondary)" }}>Cada tarjeta muestra el precio máximo de compra recalculado si el benchmark LME sube o baja ese porcentaje, manteniendo tus costes y margen fijos.</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold" style={{ color: "var(--color-text-primary)" }}>Diferencia vs. base</span>
                <span style={{ color: "var(--color-text-secondary)" }}>El número en rojo/verde bajo cada precio indica cuánto ganarías o perderías por tonelada respecto al escenario base actual. Úsalo para negociar el precio de compra con margen de seguridad.</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold" style={{ color: "var(--color-text-primary)" }}>Evaluación de riesgo</span>
                <span style={{ color: "var(--color-text-secondary)" }}>{interpretation?.riskMsg}</span>
              </div>
            </div>
          </div>

          <div className="rounded-lg p-3 border border-amber-200 bg-amber-50">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={15} color="var(--color-warning)" strokeWidth={2} />
              <div className="flex flex-col gap-1">
                <p className="text-xs" style={{ color: "var(--color-warning)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  Los escenarios simulan variaciones del precio benchmark LME. El precio máximo de compra se recalcula manteniendo el mismo yield, costes y márgenes configurados.
                </p>
                <p className="text-xs" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  ℹ️ La volatilidad histórica EUR/USD ya está incorporada en el buffer de riesgo. Este simulador permite añadir un shock adicional puntual por encima de la volatilidad histórica.
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="rounded-lg p-4 text-center" style={{ background: "var(--color-muted)" }}>
          <p className="text-sm" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            Pulse &quot;Ejecutar Stress Test&quot; para ver escenarios de mercado
          </p>
        </div>
      )}
    </div>
  );
}